import React, { useRef } from 'react';
import LinkedInGuidePDFExport from '../components/linkedin-guide/LinkedInGuidePDFExport';

const LinkedInGuide: React.FC = () => {
  const contentRef = useRef<HTMLDivElement>(null);

  // PDF export is handled by the LinkedInGuidePDFExport component

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-3xl font-bold text-blue-600">LinkedIn Networking: A Beginner's Guide</h1>
        <LinkedInGuidePDFExport contentRef={contentRef} />
      </div>

      <div ref={contentRef} className="bg-white p-6 rounded-lg shadow-lg">
        <div className="prose max-w-none">
          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-blue-800 mb-4">Why LinkedIn Matters for Your Personal Brand</h2>
            <p className="mb-4">
              LinkedIn has evolved from a simple job-search platform to the world's largest professional networking site with over 900 million members across more than 200 countries. Unlike other social media platforms, LinkedIn is specifically designed for professional networking, making it an invaluable tool for building and growing your personal brand.
            </p>
            <div className="bg-blue-50 p-4 rounded-md mb-4">
              <h3 className="font-semibold text-blue-800 mb-2">Key Benefits of LinkedIn for Personal Branding:</h3>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>Professional Visibility:</strong> Your profile serves as a dynamic digital resume that's visible to recruiters, potential clients, and industry peers.</li>
                <li><strong>Industry Credibility:</strong> Sharing thought leadership content establishes you as knowledgeable in your field.</li>
                <li><strong>Network Expansion:</strong> Connect with professionals worldwide whom you might never meet otherwise.</li>
                <li><strong>Career Opportunities:</strong> 95% of recruiters use LinkedIn to find and vet candidates.</li>
                <li><strong>Business Development:</strong> Generate leads, find partners, and identify potential clients.</li>
              </ul>
            </div>
            <p>
              Whether you're job hunting, building a business, or simply aiming to strengthen your professional reputation, LinkedIn provides tools and opportunities that no other platform can match.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-blue-800 mb-4">Getting Started with LinkedIn Networking</h2>
            
            <h3 className="text-xl font-medium text-blue-700 mb-2">1. Optimize Your Profile</h3>
            <p className="mb-4">
              Before you start networking, ensure your profile accurately represents your professional identity:
            </p>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li><strong>Professional Photo:</strong> Use a high-quality, professional headshot where your face takes up 60% of the frame.</li>
              <li><strong>Compelling Headline:</strong> Go beyond your job title to include your expertise and value proposition.</li>
              <li><strong>Engaging Summary:</strong> Write in first person and tell your professional story, highlighting achievements and career trajectory.</li>
              <li><strong>Complete Work History:</strong> Include all relevant positions with accomplishments, not just responsibilities.</li>
              <li><strong>Skills & Endorsements:</strong> List relevant skills and ask colleagues to endorse them for credibility.</li>
            </ul>
            
            <div className="bg-blue-50 p-5 rounded-lg border-l-4 border-blue-500 mb-4">
              <h4 className="font-bold text-blue-800 mb-2">Optimize Your Profile with Explanatum</h4>
              <p className="mb-2">
                Creating a compelling LinkedIn profile can be challenging. That's why <a href="https://explanatum.co.uk" className="text-blue-600 hover:underline font-medium">explanatum.co.uk</a> offers the LinkedIn Profile Enhancer tool to help you stand out.
              </p>
              <p className="mb-2">
                Our AI-powered tool helps you create:
              </p>
              <ul className="list-disc pl-6 space-y-1 mb-3">
                <li><strong>Professional Headlines</strong> that capture attention and highlight your expertise</li>
                <li><strong>Engaging Profile Summaries</strong> that tell your professional story effectively</li>
                <li><strong>Optimized Experience Descriptions</strong> that showcase achievements, not just responsibilities</li>
                <li><strong>Skill Recommendations</strong> tailored to your industry and career goals</li>
                <li><strong>Profile Visibility Tips</strong> to ensure recruiters and connections can find you</li>
              </ul>
              <p>
                <a href="/marketing/linkedin-profile-enhancer" className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors font-medium text-sm">
                  Try the LinkedIn Profile Enhancer
                  <svg className="ml-2 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                  </svg>
                </a>
              </p>
            </div>

            <h3 className="text-xl font-medium text-blue-700 mb-2">2. Understanding Your Network</h3>
            <p className="mb-4">
              LinkedIn organizes connections into three degrees:
            </p>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li><strong>1st Degree:</strong> People you're directly connected to. You can message them freely.</li>
              <li><strong>2nd Degree:</strong> People connected to your 1st-degree connections. You can request an introduction or connect directly.</li>
              <li><strong>3rd Degree:</strong> People connected to your 2nd-degree connections. Limited visibility unless you connect.</li>
            </ul>
            <p className="mb-4">
              To view your network, click on "My Network" in the top navigation. This shows your connections, pending invitations, and people you may know.
            </p>

            <h3 className="text-xl font-medium text-blue-700 mb-2">3. Growing Your Network Strategically</h3>
            <div className="bg-blue-50 p-4 rounded-md mb-4">
              <p className="mb-2"><strong>Quality Over Quantity:</strong> Focus on meaningful connections rather than collecting contacts.</p>
              <p className="mb-4">Here's how to connect strategically:</p>
              <ol className="list-decimal pl-6 space-y-2">
                <li>Start with people you know: colleagues, classmates, and industry peers.</li>
                <li>Join relevant industry groups and connect with active participants.</li>
                <li>Follow companies you're interested in and connect with their employees.</li>
                <li>After events or meetings, connect with people you met within 48 hours.</li>
                <li>Always include a personalized message with connection requests, referencing how you know them or why you want to connect.</li>
              </ol>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-blue-800 mb-4">Effective Networking Techniques</h2>
            
            <h3 className="text-xl font-medium text-blue-700 mb-2">1. Engaging with Content</h3>
            <p className="mb-4">
              The LinkedIn algorithm rewards engagement. To increase visibility:
            </p>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li>Like, comment, and share posts that resonate with you.</li>
              <li>Leave thoughtful comments that add to the conversation.</li>
              <li>Engage with content from people you want to build relationships with.</li>
            </ul>

            <h3 className="text-xl font-medium text-blue-700 mb-2">2. Creating Valuable Content</h3>
            <p className="mb-4">
              Position yourself as a thought leader by regularly sharing:
            </p>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li>Industry insights and trends</li>
              <li>Lessons from your professional experiences</li>
              <li>Helpful resources and articles</li>
              <li>Achievements and milestones (without bragging)</li>
              <li>Questions that spark meaningful discussions</li>
            </ul>

            <h3 className="text-xl font-medium text-blue-700 mb-2">3. Direct Outreach</h3>
            <p className="mb-4">
              When reaching out to new connections:
            </p>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li>Research their profile thoroughly before contacting them.</li>
              <li>Reference specific points from their experience or content they've shared.</li>
              <li>Be clear about why you're reaching out and what value you can offer.</li>
              <li>Keep messages concise and end with a clear call to action.</li>
            </ul>

            <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-4">
              <p className="font-medium">Pro Tip: Use the "People Also Viewed" sidebar on profiles to discover similar professionals who might be valuable connections.</p>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-blue-800 mb-4">Maintaining and Nurturing Your Network</h2>
            <p className="mb-4">
              Building a network is just the beginning. To derive value from your connections:
            </p>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li><strong>Stay Active:</strong> Log in regularly and engage with your feed.</li>
              <li><strong>Celebrate Milestones:</strong> Congratulate connections on promotions, new jobs, or work anniversaries.</li>
              <li><strong>Share Opportunities:</strong> Post relevant job openings, events, or resources.</li>
              <li><strong>Offer Help:</strong> Provide introductions, recommendations, or advice when appropriate.</li>
              <li><strong>Follow Up:</strong> After meaningful interactions, continue the conversation through direct messages.</li>
            </ul>
            <p>
              Remember that networking is a two-way street. The most successful networkers focus on giving value before asking for anything in return.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-blue-800 mb-4">Conclusion: Your LinkedIn Journey</h2>
            <p className="mb-4">
              LinkedIn networking isn't about collecting as many connections as possible—it's about building meaningful professional relationships that benefit everyone involved. By optimizing your profile, engaging strategically, and consistently providing value to your network, you can leverage LinkedIn to build a powerful personal brand that opens doors to new opportunities.
            </p>
            <p className="mb-4">
              Start small, be consistent, and focus on quality interactions. Over time, you'll develop a network that not only supports your career goals but also enriches your professional life through knowledge sharing and collaboration.
            </p>
            <p className="font-medium text-blue-700">
              Remember: Your personal brand on LinkedIn is built one connection, one post, and one interaction at a time. Make each one count.
            </p>
          </section>
        </div>
      </div>
      
      <div className="mt-8 text-center text-gray-600">
        <p>© 2025 LinkedIn Networking Guide. All rights reserved.</p>
      </div>
    </div>
  );
};

export default LinkedInGuide;
